/**
 * Shape of `projects.case_study` (JSONB). Every key is optional — rows created
 * by `auto_create_portfolio_draft()` or before a field existed simply omit it.
 */
export interface CaseStudy {
  challenge?: string;
  solution?: string;
  results?: string;
  gallery?: string[];
  // Metric callout shown on the Cases grid/detail (e.g. metric: "60%",
  // metricLabel: "menos tempo de resposta").
  metric?: string;
  metricLabel?: string;
  sector?: string;
  /** Fictitious brand built as a demo of the kind of site on sale. */
  concept?: boolean;
}

/** Editable, string-only view of a CaseStudy used by the admin form. */
export interface CaseStudyForm {
  sector: string;
  challenge: string;
  solution: string;
  results: string;
  metric: string;
  metricLabel: string;
  /** One image URL per line. */
  gallery: string;
  concept: boolean;
}

const TEXT_KEYS = ["sector", "challenge", "solution", "results", "metric", "metricLabel"] as const;

/** URL-safe slug: lowercase ASCII, accents stripped, words joined by "-". */
export function slugify(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/&/g, " e ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function isImageRef(value: string): boolean {
  if (value.startsWith("/") && !value.startsWith("//")) return true;
  try {
    const { protocol } = new URL(value);
    return protocol === "https:" || protocol === "http:";
  } catch {
    return false;
  }
}

/**
 * Textarea → gallery list. Keeps absolute http(s) URLs and site-relative
 * paths ("/demos/x/cover.webp"); drops blanks, anything else and duplicates.
 */
export function parseGallery(text: string): string[] {
  const seen = new Set<string>();
  for (const line of text.split(/\r?\n/)) {
    const value = line.trim();
    if (value && isImageRef(value)) seen.add(value);
  }
  return [...seen];
}

export function caseStudyToForm(caseStudy: CaseStudy | null | undefined): CaseStudyForm {
  const cs = caseStudy ?? {};
  return {
    sector: cs.sector ?? "",
    challenge: cs.challenge ?? "",
    solution: cs.solution ?? "",
    results: cs.results ?? "",
    metric: cs.metric ?? "",
    metricLabel: cs.metricLabel ?? "",
    gallery: (cs.gallery ?? []).join("\n"),
    concept: cs.concept === true,
  };
}

/**
 * Form → JSONB. Empty fields are removed (not stored as ""), and keys the
 * form doesn't know about (e.g. `metrics` from the auto-draft trigger) are
 * preserved from `base` so editing never drops data.
 */
export function buildCaseStudy(form: CaseStudyForm, base?: CaseStudy | null): CaseStudy {
  const next: Record<string, unknown> = { ...(base ?? {}) };

  for (const key of TEXT_KEYS) {
    const value = form[key].trim();
    if (value) next[key] = value;
    else delete next[key];
  }

  const gallery = parseGallery(form.gallery);
  if (gallery.length) next.gallery = gallery;
  else delete next.gallery;

  if (form.concept) next.concept = true;
  else delete next.concept;

  return next as CaseStudy;
}

export function isConceptProject(project: { case_study: CaseStudy | null }): boolean {
  return project.case_study?.concept === true;
}
